/**
 * Прогресс справочника на клиенте — зеркало серверного (`/api/reference/progress`),
 * чтобы он шёл за учеником на любое устройство.
 *
 * Действия применяются сразу (оптимистично), а на сервер уходят событием
 * («карточка — знаю», «викторина — 0.9»); ответ сервера заменяет локальное
 * состояние. Прогресс, накопленный раньше в `localStorage` (до
 * синхронизации), один раз сливается с серверным и стирается.
 */

import { useEffect } from 'react';
import { create } from 'zustand';

import {
  SANDBOX_CORE,
  SANDBOX_REQUIRED,
  sandboxTopicId,
  type CurriculumLevel,
  type LanguageCode,
  type ReferenceProgressEvent,
  type ReferenceProgressImport,
  type ReferenceProgressView,
} from '@aptile/shared';

import { fetchReferenceProgress, importReferenceProgress, sendReferenceEvent, skipReferenceSandbox } from '../api/reference';
import { EMPTY_PROGRESS, isMastered, type TopicProgress } from '../components/guide/guideGame';
import { logger } from '../lib/logger';
import { useLearningStore } from './learningStore';

/** Ключ прежнего локального прогресса (до синхронизации с сервером). */
const LEGACY_KEY = 'aptile-guide';

interface GuideState {
  /** Прогресс изучаемого языка; `null` — ещё не загружен. */
  readonly progress: ReferenceProgressView | null;
  readonly language: LanguageCode | null;
  readonly load: (language: LanguageCode) => Promise<void>;
  readonly send: (topicId: string, event: ReferenceProgressEvent) => void;
  readonly passSandbox: () => void;
}

/** То же, что сделает сервер, — чтобы интерфейс не ждал ответа. */
function applyLocally(progress: TopicProgress, event: ReferenceProgressEvent): TopicProgress {
  switch (event.kind) {
    case 'mark': {
      const rest = progress.known.filter((id) => id !== event.cardId);
      return { ...progress, known: event.known ? [...rest, event.cardId] : rest };
    }
    case 'quiz':
      return { ...progress, quizBest: Math.max(progress.quizBest, event.score) };
    case 'listen':
      return { ...progress, listenBest: Math.max(progress.listenBest, event.score) };
    case 'match':
      return { ...progress, perfectMatches: progress.perfectMatches + (event.perfect ? 1 : 0) };
    case 'reset':
      return { ...EMPTY_PROGRESS, cardCount: progress.cardCount };
  }
}

/** Прежний локальный прогресс языка, если он есть. */
function legacyProgress(language: LanguageCode): ReferenceProgressImport | null {
  try {
    const raw = localStorage.getItem(LEGACY_KEY);
    if (raw === null) return null;
    const stored = (JSON.parse(raw) as { readonly state?: { readonly topics?: Record<string, TopicProgress>; readonly sandboxPassed?: Record<string, boolean> } }).state;
    const topics = Object.fromEntries(
      Object.entries(stored?.topics ?? {})
        .filter(([id]) => id.startsWith(`${language}-`))
        .map(([id, topic]) => [id, { known: topic.known, quizBest: topic.quizBest, listenBest: topic.listenBest ?? 0, perfectMatches: topic.perfectMatches }]),
    );
    const sandboxPassed = stored?.sandboxPassed?.[language] === true;
    return Object.keys(topics).length === 0 && !sandboxPassed ? null : { topics, sandboxPassed };
  } catch {
    return null;
  }
}

/** Локальный прогресс слит с сервером — стереть, чтобы не сливать снова. */
function dropLegacy(): void {
  try {
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    // Хранилище недоступно — нечего и стирать.
  }
}

/** Одна загрузка на язык за раз: экраны вызывают `load` независимо друг от друга. */
let loading: { readonly language: LanguageCode; readonly job: Promise<void> } | null = null;

export const useGuideStore = create<GuideState>()((set, get) => ({
  progress: null,
  language: null,
  load: (language) => {
    if (loading?.language === language) return loading.job;
    const job = (async () => {
      try {
        const legacy = legacyProgress(language);
        let progress = await fetchReferenceProgress();
        if (legacy !== null) {
          progress = await importReferenceProgress(legacy);
          dropLegacy();
        }
        set({ progress, language });
      } catch (error: unknown) {
        logger.warn('Прогресс справочника не загрузился', error);
      } finally {
        loading = null;
      }
    })();
    loading = { language, job };
    return job;
  },
  send: (topicId, event) => {
    const { progress, language } = get();
    if (progress !== null) {
      const current = progress.topics[topicId] ?? EMPTY_PROGRESS;
      set({ progress: { ...progress, topics: { ...progress.topics, [topicId]: applyLocally(current, event) } } });
    }
    sendReferenceEvent(topicId, event)
      .then((fresh) => {
        if (get().language === language) set({ progress: fresh });
      })
      .catch((error: unknown) => logger.warn('Событие справочника не сохранилось', error));
  },
  passSandbox: () => {
    const { progress } = get();
    if (progress !== null) set({ progress: { ...progress, sandboxPassed: true } });
    skipReferenceSandbox()
      .then((fresh) => set({ progress: fresh }))
      .catch((error: unknown) => logger.warn('Песочница не пропустилась', error));
  },
}));

/** Прогресс справочника изучаемого языка; загружает его при первом обращении. */
export function useGuideProgress(): ReferenceProgressView | null {
  const language = useLearningStore((state) => state.activeLanguage);
  const progress = useGuideStore((state) => (state.language === language ? state.progress : null));
  const load = useGuideStore((state) => state.load);
  useEffect(() => {
    if (progress === null) void load(language);
  }, [progress, load, language]);
  return progress;
}

/** Прогресс темы; темы ещё не открывали — пустой. */
export function useTopicProgress(topicId: string): TopicProgress {
  return useGuideProgress()?.topics[topicId] ?? EMPTY_PROGRESS;
}

export interface SandboxState {
  /** Карта уроков закрыта: ученик на A1, уроков ещё нет, песочница не пройдена. */
  readonly active: boolean;
  /** Темы песочницы языка: id и освоена ли; у языка без песочницы — пусто. */
  readonly topics: readonly { readonly key: 'verbs' | 'numbers' | 'colors'; readonly id: string; readonly mastered: boolean }[];
  readonly mastered: number;
  readonly required: number;
  readonly passed: boolean;
}

/**
 * Песочница новичка для изучаемого языка. `level` и `completedLessons` —
 * с карты или сводки; пока они или прогресс не известны, карта не
 * закрывается (сервер всё равно не отдаст урок — `SANDBOX_LOCKED`).
 */
export function useSandbox(level: CurriculumLevel | null, completedLessons: number | null): SandboxState {
  const language = useLearningStore((state) => state.activeLanguage);
  const progress = useGuideProgress();
  const topics = (SANDBOX_CORE[language] ?? []).map((key) => {
    const id = sandboxTopicId(language, key);
    const entry = progress?.topics[id];
    return { key, id, mastered: entry !== undefined && isMastered(entry, entry.cardCount) };
  });
  const mastered = topics.filter((topic) => topic.mastered).length;
  const required = Math.min(SANDBOX_REQUIRED, topics.length);
  const passed = progress === null || progress.sandboxPassed || topics.length === 0 || mastered >= required;
  const active = !passed && level === 'A1' && completedLessons === 0;
  return { active, topics, mastered, required, passed };
}
